/**
 * iter213 — Stockaris Transport (TMS)
 *
 * Single page with 4 tabs: Board, Jobs, Vehicles, Drivers.
 * Board reuses the same DnD engine as the Discrepancy Kanban.
 */
import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import {
  Truck, User, Package, ArrowClockwise, Plus, Trash, Kanban, ArrowRight, Warning, Copy, CurrencyDollar, Lightning,
  Repeat, PlayCircle, Pause, Calendar,
} from '@phosphor-icons/react';
import {
  listTransportVehicles, createTransportVehicle, updateTransportVehicle, deleteTransportVehicle,
  listTransportDrivers, createTransportDriver, updateTransportDriver, deleteTransportDriver,
  listTransportJobs, createTransportJob, deleteTransportJob, transitionTransportJob,
  assignTransportJob, unassignTransportJob, getTransportBoard, issueDriverToken,
  getJobTrackingLink,
  getStopTrackingLink,
  listFreightRates, createFreightRate, updateFreightRate, deleteFreightRate, rateJob,
  importRatesPreview, importRatesCommit, getDriverEarnings, markDriverPaid, markDriverUnpaid,
  suggestVehicle, driverEarningsPdfUrl,
  listFuelReceipts, addFuelReceipt, deleteFuelReceipt,
  listEmptyLegs, createEmptyLeg, transitionEmptyLeg, deleteEmptyLeg,
  listExceptionReasons, listExceptions, getTransportAnalytics,
  listRecurringTemplates, createRecurringTemplate, updateRecurringTemplate,
  deleteRecurringTemplate, pauseRecurringTemplate, previewRecurringTemplate, spawnRecurringTemplate,
} from '../lib/api';
import RoutePlannerDialog from '../components/RoutePlannerDialog';

const STATE_COLORS = {
  new: 'bg-slate-100 text-slate-800 border-slate-300',
  planned: 'bg-blue-100 text-blue-800 border-blue-300',
  in_transit: 'bg-amber-100 text-amber-800 border-amber-300',
  delivered: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  billed: 'bg-purple-100 text-purple-800 border-purple-300',
  cancelled: 'bg-gray-200 text-gray-700 border-gray-300',
};

// ---------------------------------------------------------------------------
// Vehicles Tab
// ---------------------------------------------------------------------------
function VehiclesTab() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [fuelVehicle, setFuelVehicle] = useState(null);
  const [form, setForm] = useState({
    registration: '', vehicle_type: 'rigid', payload_kg: 0,
    cubic_m3: 0, pallet_capacity: 0, refrigerated: false, tailgate: false,
    dg_approved: false, route_profile: 'driving-hgv', status: 'active',
  });
  const load = useCallback(async () => {
    const r = await listTransportVehicles();
    setRows(r.data.vehicles);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await createTransportVehicle(form);
      toast.success(`Vehicle ${form.registration} created`);
      setForm({ ...form, registration: '' });
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
  };
  const remove = async (id, reg) => {
    if (!window.confirm(`Delete vehicle ${reg}?`)) return;
    try { await deleteTransportVehicle(id); load(); toast.success('Deleted'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-3" data-testid="transport-vehicles-tab">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">{rows.length} vehicle(s)</div>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="tp-vehicle-new-btn">
          <Plus size={14} className="mr-1" /> New vehicle
        </Button>
      </div>
      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-left">
            <tr><th className="p-2">Rego</th><th className="p-2">Type</th><th className="p-2">Pallets</th><th className="p-2">Weight (kg)</th><th className="p-2">Cubic (m³)</th><th className="p-2">Profile</th><th className="p-2">Fridge</th><th className="p-2">Tailgate</th><th className="p-2">DG</th><th className="p-2">Fuel c/km</th><th className="p-2">Status</th><th className="p-2"></th></tr>
          </thead>
          <tbody>
            {rows.map((v) => (
              <tr key={v.id} className="border-t border-border" data-testid={`tp-vehicle-row-${v.id}`}>
                <td className="p-2 font-mono font-semibold">{v.registration}</td>
                <td className="p-2">{v.vehicle_type}</td>
                <td className="p-2 tabular-nums">{v.pallet_capacity}</td>
                <td className="p-2 tabular-nums">{v.payload_kg}</td>
                <td className="p-2 tabular-nums">{v.cubic_m3}</td>
                <td className="p-2 text-[10px] font-mono">{v.route_profile || 'driving-hgv'}</td>
                <td className="p-2">{v.refrigerated ? '❄️' : '—'}</td>
                <td className="p-2">{v.tailgate ? '✓' : '—'}</td>
                <td className="p-2">{v.dg_approved ? '⚠️' : '—'}</td>
                <td className="p-2 tabular-nums text-[10px]" data-testid={`tp-vehicle-fuel-cpk-${v.id}`}>
                  {v.fuel_cpk != null ? `${v.fuel_cpk.toFixed(1)}¢` : <span className="text-muted-foreground">—</span>}
                  {v.fuel_alert && (
                    <div className="mt-0.5" data-testid={`tp-vehicle-fuel-alert-${v.id}`}>
                      <Badge className="text-[9px] bg-red-100 text-red-800 border-red-300"><Warning size={9} weight="fill" className="mr-0.5" />+{v.fuel_alert.pct_up}%</Badge>
                    </div>
                  )}
                </td>
                <td className="p-2"><Badge variant="outline" className="text-[10px]">{v.status}</Badge>{v.operational_status && <div className="mt-0.5"><Badge className="text-[9px] bg-slate-100 text-slate-700 border-slate-300" data-testid={`tp-vehicle-ops-${v.id}`}>{v.operational_status}</Badge></div>}</td>
                <td className="p-2 whitespace-nowrap">
                  <Button variant="ghost" size="xs" onClick={() => setFuelVehicle(v)} data-testid={`tp-vehicle-fuel-${v.id}`} title="Fuel receipts + cents-per-km">Fuel</Button>
                  <Button variant="ghost" size="xs" onClick={() => remove(v.id, v.registration)} data-testid={`tp-vehicle-delete-${v.id}`}><Trash size={12} /></Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={12} className="p-6 text-center text-muted-foreground italic">No vehicles yet</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="tp-vehicle-dialog">
          <DialogHeader><DialogTitle>New vehicle</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Registration *</Label><Input value={form.registration} onChange={(e) => setForm({ ...form, registration: e.target.value })} data-testid="tp-vehicle-form-rego" /></div>
            <div><Label className="text-xs">Type</Label>
              <select className="w-full h-9 rounded border border-border px-2 text-sm" value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}>
                <option>rigid</option><option>semi</option><option>b-double</option><option>van</option><option>ute</option>
              </select>
            </div>
            <div><Label className="text-xs">Pallet capacity</Label><Input type="number" value={form.pallet_capacity} onChange={(e) => setForm({ ...form, pallet_capacity: +e.target.value })} data-testid="tp-vehicle-form-pallets" /></div>
            <div><Label className="text-xs">Payload (kg)</Label><Input type="number" value={form.payload_kg} onChange={(e) => setForm({ ...form, payload_kg: +e.target.value })} data-testid="tp-vehicle-form-weight" /></div>
            <div><Label className="text-xs">Cubic (m³)</Label><Input type="number" value={form.cubic_m3} onChange={(e) => setForm({ ...form, cubic_m3: +e.target.value })} data-testid="tp-vehicle-form-cubic" /></div>
            <div><Label className="text-xs">Route profile</Label>
              <select className="w-full h-9 rounded border border-border px-2 text-sm" value={form.route_profile} onChange={(e) => setForm({ ...form, route_profile: e.target.value })} data-testid="tp-vehicle-form-profile">
                <option value="driving-hgv">driving-hgv (truck)</option>
                <option value="driving-car">driving-car (van/car)</option>
              </select>
            </div>
            <div className="flex gap-3 items-end pb-1">
              <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={form.refrigerated} onChange={(e) => setForm({ ...form, refrigerated: e.target.checked })} /> Fridge</label>
              <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={form.tailgate} onChange={(e) => setForm({ ...form, tailgate: e.target.checked })} /> Tailgate</label>
              <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={form.dg_approved} onChange={(e) => setForm({ ...form, dg_approved: e.target.checked })} /> DG</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="tp-vehicle-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <FuelReceiptsDialog vehicle={fuelVehicle} onClose={() => { setFuelVehicle(null); load(); }} />
    </div>
  );
}

// iter221 — Fuel Receipts dialog
function FuelReceiptsDialog({ vehicle, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const empty = { date: new Date().toISOString().slice(0, 10), litres: 0, total_cost: 0, odometer_km: 0, station: '' };
  const [form, setForm] = useState(empty);
  const load = useCallback(async () => {
    if (!vehicle) return;
    try { const r = await listFuelReceipts(vehicle.id); setData(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
  }, [vehicle]);
  useEffect(() => { if (vehicle) { setForm(empty); load(); } }, [vehicle, load]);

  const add = async () => {
    if (!form.litres || !form.total_cost) return toast.error('Litres + total cost required');
    setBusy(true);
    try {
      await addFuelReceipt(vehicle.id, {
        date: form.date, litres: +form.litres, total_cost: +form.total_cost,
        odometer_km: form.odometer_km ? +form.odometer_km : null,
        station: form.station || null,
      });
      setForm(empty);
      load();
      toast.success('Receipt added');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Add failed'); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete receipt?')) return;
    await deleteFuelReceipt(vehicle.id, id);
    load();
  };

  return (
    <Dialog open={!!vehicle} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh]" data-testid="tp-fuel-dialog">
        <DialogHeader>
          <DialogTitle>Fuel — {vehicle?.registration}</DialogTitle>
          <DialogDescription>
            Log every fuel receipt. Cents-per-km is computed from total_cost ÷ odometer span across all receipts.
          </DialogDescription>
        </DialogHeader>
        {data?.vehicle?.fuel_alert && (
          <div className="rounded-md border border-red-300 bg-red-50 text-red-900 px-3 py-2 text-xs flex items-center gap-2" data-testid="tp-fuel-alert-banner">
            <Warning size={14} weight="fill" className="text-red-600 shrink-0" />
            <span className="font-medium">{data.vehicle.fuel_alert.message}</span>
          </div>
        )}
        {data && (
          <div className="grid grid-cols-4 gap-2 mb-2 text-center text-xs">
            <div className="border rounded p-2"><div className="text-lg font-bold tabular-nums" data-testid="tp-fuel-cpk">{data.stats.fuel_cpk != null ? `${data.stats.fuel_cpk.toFixed(1)}¢` : '—'}</div><div className="text-[10px] text-muted-foreground uppercase">cents / km</div></div>
            <div className="border rounded p-2"><div className="text-lg font-bold tabular-nums">${(data.stats.total_cost || 0).toFixed(2)}</div><div className="text-[10px] text-muted-foreground uppercase">total spend</div></div>
            <div className="border rounded p-2"><div className="text-lg font-bold tabular-nums">{(data.stats.total_litres || 0).toFixed(0)} L</div><div className="text-[10px] text-muted-foreground uppercase">total litres</div></div>
            <div className="border rounded p-2"><div className="text-lg font-bold tabular-nums">{data.stats.km_span != null ? `${data.stats.km_span.toFixed(0)} km` : '—'}</div><div className="text-[10px] text-muted-foreground uppercase">odo span</div></div>
          </div>
        )}
        <div className="border border-slate-200 rounded p-2 mb-2 grid grid-cols-6 gap-2 items-end bg-slate-50">
          <div><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="tp-fuel-date" /></div>
          <div><Label className="text-xs">Litres</Label><Input type="number" step="0.1" value={form.litres} onChange={(e) => setForm({ ...form, litres: e.target.value })} data-testid="tp-fuel-litres" /></div>
          <div><Label className="text-xs">Total $</Label><Input type="number" step="0.01" value={form.total_cost} onChange={(e) => setForm({ ...form, total_cost: e.target.value })} data-testid="tp-fuel-cost" /></div>
          <div><Label className="text-xs">Odometer km</Label><Input type="number" value={form.odometer_km} onChange={(e) => setForm({ ...form, odometer_km: e.target.value })} data-testid="tp-fuel-odo" /></div>
          <div><Label className="text-xs">Station</Label><Input value={form.station} onChange={(e) => setForm({ ...form, station: e.target.value })} placeholder="BP" /></div>
          <Button size="sm" onClick={add} disabled={busy} data-testid="tp-fuel-add">Add receipt</Button>
        </div>
        <div className="overflow-y-auto max-h-[45vh]">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-left sticky top-0">
              <tr><th className="p-2">Date</th><th className="p-2">Litres</th><th className="p-2">Total</th><th className="p-2">c/L</th><th className="p-2">Odo km</th><th className="p-2">Station</th><th className="p-2"></th></tr>
            </thead>
            <tbody>
              {(data?.receipts || []).map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`tp-fuel-row-${r.id}`}>
                  <td className="p-2 tabular-nums">{r.date}</td>
                  <td className="p-2 tabular-nums">{r.litres?.toFixed(1)}</td>
                  <td className="p-2 tabular-nums">${r.total_cost?.toFixed(2)}</td>
                  <td className="p-2 tabular-nums">{r.cents_per_litre?.toFixed(1)}</td>
                  <td className="p-2 tabular-nums">{r.odometer_km || '—'}</td>
                  <td className="p-2">{r.station || '—'}</td>
                  <td className="p-2"><Button variant="ghost" size="xs" onClick={() => remove(r.id)} data-testid={`tp-fuel-delete-${r.id}`}><Trash size={12} /></Button></td>
                </tr>
              ))}
              {(!data || data.receipts.length === 0) && <tr><td colSpan={7} className="p-4 text-center text-muted-foreground italic">No receipts yet</td></tr>}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Drivers Tab
// ---------------------------------------------------------------------------
function DriversTab() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [earningsDriver, setEarningsDriver] = useState(null);
  const [form, setForm] = useState({ name: '', mobile: '', email: '', licence_class: '', employment_type: 'employee', subcontractor_name: '', abn: '', available: true });
  const load = useCallback(async () => setRows((await listTransportDrivers()).data.drivers), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await createTransportDriver(form);
      toast.success('Driver created');
      setForm({ name: '', mobile: '', email: '', licence_class: '', employment_type: 'employee', subcontractor_name: '', abn: '', available: true });
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
  };
  const remove = async (id, name) => {
    if (!window.confirm(`Delete driver ${name}?`)) return;
    try { await deleteTransportDriver(id); load(); toast.success('Deleted'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };
  const copyLink = async (id, name) => {
    try {
      const r = await issueDriverToken(id);
      const url = `${window.location.origin}/driver/t/${r.data.token}`;
      await navigator.clipboard.writeText(url);
      toast.success(`Driver link copied for ${name} (expires ${r.data.expires_in_days}d)`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Token failed'); }
  };

  return (
    <div className="space-y-3" data-testid="transport-drivers-tab">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">{rows.length} driver(s)</div>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="tp-driver-new-btn"><Plus size={14} className="mr-1" /> New driver</Button>
      </div>
      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-left"><tr><th className="p-2">Name</th><th className="p-2">Mobile</th><th className="p-2">Licence</th><th className="p-2">Type</th><th className="p-2">Available</th><th className="p-2"></th></tr></thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id} className="border-t border-border" data-testid={`tp-driver-row-${d.id}`}>
                <td className="p-2 font-semibold">{d.name}</td>
                <td className="p-2">{d.mobile || '—'}</td>
                <td className="p-2">{d.licence_class || '—'}</td>
                <td className="p-2"><Badge variant="outline" className="text-[10px]">{d.employment_type}</Badge></td>
                <td className="p-2">{d.available ? '✓' : '—'}{d.operational_status && <div className="mt-0.5"><Badge className="text-[9px] bg-slate-100 text-slate-700 border-slate-300" data-testid={`tp-driver-ops-${d.id}`}>{d.operational_status}</Badge></div>}</td>
                <td className="p-2 flex gap-1">
                  <Button variant="outline" size="xs" onClick={() => setEarningsDriver(d)} data-testid={`tp-driver-earnings-${d.id}`} title="Weekly settlement">$</Button>
                  <Button variant="outline" size="xs" onClick={() => copyLink(d.id, d.name)} data-testid={`tp-driver-link-${d.id}`}>Link</Button>
                  <Button variant="ghost" size="xs" onClick={() => remove(d.id, d.name)} data-testid={`tp-driver-delete-${d.id}`}><Trash size={12} /></Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground italic">No drivers yet</td></tr>}
          </tbody>
        </table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="tp-driver-dialog">
          <DialogHeader><DialogTitle>New driver</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tp-driver-form-name" /></div>
            <div><Label className="text-xs">Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
            <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="tp-driver-form-email" /></div>
            <div><Label className="text-xs">Licence class</Label><Input value={form.licence_class} onChange={(e) => setForm({ ...form, licence_class: e.target.value })} /></div>
            <div><Label className="text-xs">Employment</Label>
              <select className="w-full h-9 rounded border border-border px-2 text-sm" value={form.employment_type} onChange={(e) => setForm({ ...form, employment_type: e.target.value })}>
                <option>employee</option><option>subcontractor</option>
              </select>
            </div>
            {form.employment_type === 'subcontractor' && (
              <>
                <div><Label className="text-xs">Trading name</Label><Input value={form.subcontractor_name} onChange={(e) => setForm({ ...form, subcontractor_name: e.target.value })} placeholder="e.g. ACME Transport Pty Ltd" data-testid="tp-driver-form-tradename" /></div>
                <div><Label className="text-xs">ABN</Label><Input value={form.abn} onChange={(e) => setForm({ ...form, abn: e.target.value })} placeholder="12 345 678 901" data-testid="tp-driver-form-abn" /></div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="tp-driver-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DriverEarningsDialog driver={earningsDriver} onClose={() => setEarningsDriver(null)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// iter219 — Driver Earnings dialog
// ---------------------------------------------------------------------------
function DriverEarningsDialog({ driver, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const load = useCallback(async () => {
    if (!driver) return;
    setLoading(true);
    try {
      const params = {};
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      const r = await getDriverEarnings(driver.id, params);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, [driver, from, to]);
  useEffect(() => { if (driver) load(); }, [driver, load]);

  const togglePaid = async (entry) => {
    try {
      if (entry.paid_at) await markDriverUnpaid(entry.job_id);
      else await markDriverPaid(entry.job_id, { invoice_ref: window.prompt('Invoice reference (optional)') || null });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };
  const exportCsv = () => {
    if (!data) return;
    const lines = ['Job,Customer,Delivery,Planned date,Pay,Paid at,Invoice ref'];
    data.entries.forEach((e) => {
      lines.push([e.job_number, `"${e.customer_name || ''}"`, `"${e.delivery_location || ''}"`,
                  e.planned_date || '', e.pay_amount.toFixed(2), e.paid_at || '', e.invoice_ref || ''].join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `earnings-${driver.name.replace(/\s+/g, '_')}.csv`;
    a.click();
  };

  return (
    <Dialog open={!!driver} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh]" data-testid="tp-earnings-dialog">
        <DialogHeader>
          <DialogTitle>Earnings ledger — {driver?.name}</DialogTitle>
          <DialogDescription>
            Uses each job's <span className="font-mono">freight_cost</span> as the driver's pay. Mark paid after settlement so the outstanding total drops.
          </DialogDescription>
        </DialogHeader>
        {data && (
          <>
            <div className="grid grid-cols-4 gap-2 mb-2">
              <div className="border border-slate-200 rounded p-2 text-center">
                <div className="text-lg font-bold tabular-nums" data-testid="tp-earnings-count">{data.totals.job_count}</div>
                <div className="text-[10px] text-muted-foreground uppercase">jobs</div>
              </div>
              <div className="border border-slate-200 rounded p-2 text-center">
                <div className="text-lg font-bold tabular-nums">${data.totals.billable.toFixed(2)}</div>
                <div className="text-[10px] text-muted-foreground uppercase">billable</div>
              </div>
              <div className="border border-emerald-200 rounded p-2 text-center bg-emerald-50">
                <div className="text-lg font-bold tabular-nums text-emerald-700" data-testid="tp-earnings-paid">${data.totals.paid.toFixed(2)}</div>
                <div className="text-[10px] text-emerald-700 uppercase">paid</div>
              </div>
              <div className="border border-amber-200 rounded p-2 text-center bg-amber-50">
                <div className="text-lg font-bold tabular-nums text-amber-800" data-testid="tp-earnings-outstanding">${data.totals.outstanding.toFixed(2)}</div>
                <div className="text-[10px] text-amber-800 uppercase">outstanding</div>
              </div>
            </div>
            <div className="flex gap-2 items-end mb-2">
              <div><Label className="text-xs">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="tp-earnings-from" /></div>
              <div><Label className="text-xs">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="tp-earnings-to" /></div>
              <Button size="sm" variant="outline" onClick={load} data-testid="tp-earnings-refresh"><ArrowClockwise size={12} className="mr-1" /> Refresh</Button>
              <Button size="sm" variant="outline" onClick={exportCsv} data-testid="tp-earnings-export">Export CSV</Button>
              <a
                href={driverEarningsPdfUrl(driver.id, from || undefined, to || undefined)}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 h-8 px-3 text-xs rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
                data-testid="tp-earnings-pdf"
              >Remittance PDF</a>
            </div>
          </>
        )}
        <div className="overflow-y-auto max-h-[50vh]">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-left sticky top-0">
              <tr>
                <th className="p-2">Job</th><th className="p-2">Customer</th>
                <th className="p-2">Delivery</th><th className="p-2">Planned</th>
                <th className="p-2 text-right">Pay</th><th className="p-2">Paid</th>
                <th className="p-2">Invoice</th><th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {(data?.entries || []).map((e) => (
                <tr key={e.job_id} className="border-t border-border" data-testid={`tp-earnings-row-${e.job_id}`}>
                  <td className="p-2 font-mono font-semibold">{e.job_number}</td>
                  <td className="p-2">{e.customer_name || '—'}</td>
                  <td className="p-2">{e.delivery_location || '—'}</td>
                  <td className="p-2 tabular-nums">{e.planned_date || '—'}</td>
                  <td className="p-2 text-right tabular-nums">${e.pay_amount.toFixed(2)}</td>
                  <td className="p-2">{e.paid_at ? <Badge className="text-[9px] bg-emerald-100 text-emerald-800 border-emerald-300">paid</Badge> : <span className="text-muted-foreground">—</span>}</td>
                  <td className="p-2 font-mono text-[10px]">{e.invoice_ref || '—'}</td>
                  <td className="p-2">
                    <Button size="xs" variant={e.paid_at ? 'ghost' : 'outline'} onClick={() => togglePaid(e)} data-testid={`tp-earnings-toggle-${e.job_id}`}>
                      {e.paid_at ? 'Unmark' : 'Mark paid'}
                    </Button>
                  </td>
                </tr>
              ))}
              {(!data || data.entries.length === 0) && (
                <tr><td colSpan={8} className="p-6 text-center text-muted-foreground italic">
                  {loading ? 'Loading…' : 'No completed jobs in this window'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Jobs Tab
// ---------------------------------------------------------------------------
function JobsTab() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    source_type: 'manual', customer_name: '', customer_email: '', pickup_location: '', delivery_location: '',
    required_date: '', window_start: '', window_end: '', pallets: 0, cartons: 0, weight_kg: 0, cubic_m3: 0,
    dangerous_goods: false, temperature: 'ambient', priority: 'normal', special_instructions: '',
    carrier_cost: 0, fuel_cost: 0, tolls_cost: 0,
  });
  const load = useCallback(async () => setRows((await listTransportJobs({})).data.jobs), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.customer_name) return toast.error('Customer required');
    try { await createTransportJob(form); toast.success('Job created'); setOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete job?')) return;
    await deleteTransportJob(id); load();
  };
  const copyTrackingLink = async (job) => {
    try {
      const r = await getJobTrackingLink(job.id);
      const url = `${window.location.origin}${r.data.path}`;
      await navigator.clipboard.writeText(url);
      toast.success(`Tracking link copied for ${job.job_number}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not copy link'); }
  };
  const copyStopTrackingLink = async (job, seq, customerName) => {
    try {
      const r = await getStopTrackingLink(job.id, seq);
      const url = `${window.location.origin}${r.data.path}`;
      await navigator.clipboard.writeText(url);
      toast.success(`Stop ${seq}${customerName ? ` · ${customerName}` : ''} tracking link copied`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not copy link'); }
  };
  const applyRate = async (job) => {
    try {
      const r = await rateJob(job.id);
      toast.success(`${job.job_number}: quoted $${r.data.priced.quoted_revenue.toFixed(2)} · margin $${r.data.margin.toFixed(2)}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Rate lookup failed'); }
  };
  const suggestForJob = async (job) => {
    try {
      const r = await suggestVehicle(job.id, job.required_date || null);
      const top = (r.data.suggestions || [])[0];
      if (!top) {
        toast.error(r.data.blocked_count > 0 ? `No eligible truck (${r.data.blocked_count} blocked)` : 'No trucks available');
        return;
      }
      toast.success(`Suggested: ${top.vehicle_registration}${top.driver_name ? ` · ${top.driver_name}` : ''} — ${top.reasons[0] || 'best fit'}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Suggestion failed'); }
  };

  return (
    <div className="space-y-3" data-testid="transport-jobs-tab">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">{rows.length} job(s)</div>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="tp-job-new-btn"><Plus size={14} className="mr-1" /> New job</Button>
      </div>
      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-left"><tr><th className="p-2">Job #</th><th className="p-2">Customer</th><th className="p-2">Pickup</th><th className="p-2">Delivery</th><th className="p-2">Date</th><th className="p-2">Plt</th><th className="p-2">Kg</th><th className="p-2">Status</th><th className="p-2">Margin</th><th className="p-2">Source</th><th className="p-2"></th></tr></thead>
          <tbody>
            {rows.map((j) => (
              <tr key={j.id} className="border-t border-border" data-testid={`tp-job-row-${j.id}`}>
                <td className="p-2 font-mono font-semibold">{j.job_number}</td>
                <td className="p-2">{j.customer_name || '—'}{j.customer_email ? <div className="text-[9px] text-muted-foreground">{j.customer_email}</div> : null}</td>
                <td className="p-2">{j.pickup_location || '—'}</td>
                <td className="p-2">
                  {j.delivery_location || '—'}
                  {j.status === 'new' && j.suggested_vehicle_id && (
                    <div className="text-[9px] text-sky-800 mt-0.5" data-testid={`tp-job-suggestion-${j.id}`}>
                      <Lightning size={9} className="inline mr-0.5" weight="fill" />
                      Suggested truck saved
                    </div>
                  )}
                </td>
                <td className="p-2 tabular-nums">{j.required_date || '—'}{j.window_start ? <div className="text-[9px]">{j.window_start}–{j.window_end || ''}</div> : null}</td>
                <td className="p-2 tabular-nums">{j.pallets}</td>
                <td className="p-2 tabular-nums">{j.weight_kg}</td>
                <td className="p-2"><Badge className={`text-[10px] ${STATE_COLORS[j.status] || ''}`}>{j.status}</Badge>{j.live_window_breach === 'late' && <div className="mt-0.5"><Badge className="text-[9px] bg-red-100 text-red-800 border-red-300"><Warning size={9} className="mr-0.5" />late</Badge></div>}</td>
                <td className="p-2 tabular-nums text-[10px]" data-testid={`tp-job-margin-${j.id}`}>
                  {j.quoted_revenue != null ? (
                    <div>
                      <div>${(j.quoted_revenue || 0).toFixed(2)}</div>
                      <div className={j.margin >= 0 ? 'text-emerald-700' : 'text-red-700'}>Δ ${(j.margin || 0).toFixed(2)}</div>
                    </div>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="p-2"><Badge variant="outline" className="text-[10px]">{j.source_type}</Badge></td>
                <td className="p-2 whitespace-nowrap">
                  <Button variant="ghost" size="xs" onClick={() => suggestForJob(j)} data-testid={`tp-job-suggest-${j.id}`} title="Suggest best-fit truck"><Lightning size={12} /></Button>
                  <Button variant="ghost" size="xs" onClick={() => applyRate(j)} data-testid={`tp-job-rate-${j.id}`} title="Apply best-matching freight rate"><CurrencyDollar size={12} /></Button>
                  {j.multi_stop && Array.isArray(j.stops) && j.stops.length > 0 ? (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="ghost" size="xs" data-testid={`tp-job-track-${j.id}`} title="Copy per-stop tracking link"><Copy size={12} /></Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-72 p-2" align="end" data-testid={`tp-job-stop-track-popover-${j.id}`}>
                        <div className="text-[11px] text-muted-foreground font-semibold mb-1 px-1">Per-consignee tracking links</div>
                        <div className="max-h-60 overflow-y-auto space-y-1">
                          {[...j.stops].sort((a, b) => (a.sequence || 0) - (b.sequence || 0)).map((s) => (
                            <button
                              key={s.sequence}
                              onClick={() => copyStopTrackingLink(j, s.sequence, s.customer_name)}
                              className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 text-[11px] flex items-center gap-2"
                              data-testid={`tp-job-stop-track-${j.id}-${s.sequence}`}
                            >
                              <span className="font-mono text-slate-500 w-5">#{s.sequence}</span>
                              <span className="flex-1 min-w-0 truncate">{s.customer_name || s.address || '—'}</span>
                              <Copy size={10} className="text-slate-400" />
                            </button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <Button variant="ghost" size="xs" onClick={() => copyTrackingLink(j)} data-testid={`tp-job-track-${j.id}`} title="Copy customer tracking link"><Copy size={12} /></Button>
                  )}
                  <Button variant="ghost" size="xs" onClick={() => remove(j.id)} data-testid={`tp-job-delete-${j.id}`}><Trash size={12} /></Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={11} className="p-6 text-center text-muted-foreground italic">No jobs yet</td></tr>}
          </tbody>
        </table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="tp-job-dialog">
          <DialogHeader>
            <DialogTitle>New transport job</DialogTitle>
            <DialogDescription>Stockaris will attempt to pre-fill from the WMS when source_type is Order or Transfer via the Auto-Create API.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 max-h-[60vh] overflow-y-auto">
            <div><Label className="text-xs">Source</Label>
              <select className="w-full h-9 rounded border border-border px-2 text-sm" value={form.source_type} onChange={(e) => setForm({ ...form, source_type: e.target.value })} data-testid="tp-job-form-source">
                <option>manual</option><option>order</option><option>transfer</option><option>3pl_request</option><option>recurring</option><option>pickup</option><option>return</option>
              </select>
            </div>
            <div><Label className="text-xs">Priority</Label>
              <select className="w-full h-9 rounded border border-border px-2 text-sm" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                <option>low</option><option>normal</option><option>high</option><option>urgent</option>
              </select>
            </div>
            <div className="col-span-2"><Label className="text-xs">Customer *</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="tp-job-form-customer" /></div>
            <div className="col-span-2"><Label className="text-xs">Customer email (tracking + delivery notifications)</Label><Input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} placeholder="you@example.com" data-testid="tp-job-form-email" /></div>
            <div><Label className="text-xs">Pickup</Label><Input value={form.pickup_location} onChange={(e) => setForm({ ...form, pickup_location: e.target.value })} /></div>
            <div><Label className="text-xs">Delivery</Label><Input value={form.delivery_location} onChange={(e) => setForm({ ...form, delivery_location: e.target.value })} data-testid="tp-job-form-delivery" /></div>
            <div><Label className="text-xs">Required date</Label><Input type="date" value={form.required_date} onChange={(e) => setForm({ ...form, required_date: e.target.value })} data-testid="tp-job-form-date" /></div>
            <div className="grid grid-cols-2 gap-1">
              <div><Label className="text-xs">Window start</Label><Input type="time" value={form.window_start} onChange={(e) => setForm({ ...form, window_start: e.target.value })} data-testid="tp-job-form-window-start" /></div>
              <div><Label className="text-xs">Window end</Label><Input type="time" value={form.window_end} onChange={(e) => setForm({ ...form, window_end: e.target.value })} data-testid="tp-job-form-window-end" /></div>
            </div>
            <div><Label className="text-xs">Temperature</Label>
              <select className="w-full h-9 rounded border border-border px-2 text-sm" value={form.temperature} onChange={(e) => setForm({ ...form, temperature: e.target.value })}>
                <option>ambient</option><option>chilled</option><option>frozen</option>
              </select>
            </div>
            <div><Label className="text-xs">Pallets</Label><Input type="number" value={form.pallets} onChange={(e) => setForm({ ...form, pallets: +e.target.value })} data-testid="tp-job-form-pallets" /></div>
            <div><Label className="text-xs">Weight (kg)</Label><Input type="number" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: +e.target.value })} data-testid="tp-job-form-weight" /></div>
            <div><Label className="text-xs">Cubic (m³)</Label><Input type="number" value={form.cubic_m3} onChange={(e) => setForm({ ...form, cubic_m3: +e.target.value })} /></div>
            <div className="flex items-end pb-1"><label className="text-xs flex items-center gap-1"><input type="checkbox" checked={form.dangerous_goods} onChange={(e) => setForm({ ...form, dangerous_goods: e.target.checked })} /> Dangerous goods</label></div>
            <div className="col-span-2"><Label className="text-xs">Special instructions</Label><Input value={form.special_instructions} onChange={(e) => setForm({ ...form, special_instructions: e.target.value })} /></div>
            <div className="col-span-2 mt-1 text-[10px] text-muted-foreground border-t border-slate-100 pt-2">Cost breakdown (roll up into freight_cost)</div>
            <div><Label className="text-xs">Carrier $</Label><Input type="number" step="0.01" value={form.carrier_cost} onChange={(e) => setForm({ ...form, carrier_cost: +e.target.value })} data-testid="tp-job-form-carrier-cost" /></div>
            <div><Label className="text-xs">Fuel $</Label><Input type="number" step="0.01" value={form.fuel_cost} onChange={(e) => setForm({ ...form, fuel_cost: +e.target.value })} data-testid="tp-job-form-fuel-cost" /></div>
            <div><Label className="text-xs">Tolls $</Label><Input type="number" step="0.01" value={form.tolls_cost} onChange={(e) => setForm({ ...form, tolls_cost: +e.target.value })} data-testid="tp-job-form-tolls-cost" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="tp-job-submit">Create job</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Planning Board Tab (Kanban with DnD)
// ---------------------------------------------------------------------------
function BoardTab() {
  const [board, setBoard] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [dragging, setDragging] = useState(null);   // { job, from_vehicle_id }
  const [dragOver, setDragOver] = useState(null);   // vehicle_id or 'unassigned'
  const [plannerVehicle, setPlannerVehicle] = useState(null);

  const load = useCallback(async () => {
    const r = await getTransportBoard(date);
    setBoard(r.data);
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const handleDragStart = (e, job, from_vehicle_id) => {
    setDragging({ job, from_vehicle_id });
    try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', job.id); } catch (_) {}
  };
  const handleDragEnd = () => { setDragging(null); setDragOver(null); };
  const handleDragOver = (e, target) => {
    if (!dragging) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOver !== target) setDragOver(target);
  };
  const handleDrop = async (e, targetColumn) => {
    e.preventDefault();
    const drag = dragging;
    setDragging(null); setDragOver(null);
    if (!drag) return;
    const targetVehicleId = targetColumn.vehicle_id;
    // Same-column drop → no-op
    if (drag.from_vehicle_id === targetVehicleId) return;

    try {
      if (!targetVehicleId) {
        // Drop back onto unassigned column
        await unassignTransportJob(drag.job.id);
        toast.success(`Unassigned ${drag.job.job_number}`);
      } else {
        // Assign to a truck. Use the first driver already on that truck as default, else null.
        const driverId = targetColumn.driver?.id || null;
        try {
          await assignTransportJob(drag.job.id, {
            vehicle_id: targetVehicleId,
            driver_id: driverId,
            planned_date: date,
          });
          toast.success(`Assigned ${drag.job.job_number} → ${targetColumn.vehicle.registration}`);
        } catch (err) {
          const d = err?.response?.data?.detail;
          if (d && typeof d === 'object' && d.warnings) {
            // Over-capacity → offer to force
            if (window.confirm(`${d.detail}\n\n${d.warnings.join('\n')}\n\nOverride and assign anyway?`)) {
              await assignTransportJob(drag.job.id, {
                vehicle_id: targetVehicleId, driver_id: driverId, planned_date: date, force: true,
              });
              toast.warning(`Assigned OVER CAPACITY: ${drag.job.job_number}`);
            } else {
              return;
            }
          } else {
            throw err;
          }
        }
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Assign failed');
    }
  };

  const advance = async (job, target) => {
    try {
      await transitionTransportJob(job.id, { target_state: target });
      toast.success(`${job.job_number} → ${target}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Transition failed'); }
  };

  if (!board) return <div className="text-sm text-muted-foreground p-4" data-testid="tp-board-loading">Loading…</div>;

  return (
    <div className="space-y-3" data-testid="transport-board-tab">
      <div className="flex flex-wrap items-center gap-2">
        <Label className="text-xs">Date</Label>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" data-testid="tp-board-date" />
        <Badge variant="outline" className="text-xs" data-testid="tp-board-unassigned-count">
          {board.totals.unassigned} unassigned
        </Badge>
        <Badge variant="outline" className="text-xs" data-testid="tp-board-planned-count">
          {board.totals.planned} planned on this date
        </Badge>
        <div className="text-[10px] text-muted-foreground">· Drag a job onto a truck to plan it</div>
        <div className="ml-auto"><Button size="sm" variant="outline" onClick={load} data-testid="tp-board-refresh"><ArrowClockwise size={14} className="mr-1" /> Refresh</Button></div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {board.columns.map((col) => {
          const isTarget = dragging && (
            (dragOver === (col.vehicle_id || 'unassigned'))
          );
          const cls = isTarget
            ? 'ring-2 ring-brand bg-brand/5'
            : col.over_capacity
              ? 'ring-1 ring-red-200'
              : '';
          const key = col.vehicle_id || 'unassigned';
          return (
            <div
              key={key}
              className={`rounded-lg p-2 border border-slate-200 bg-slate-50 min-h-[260px] transition-colors ${cls}`}
              data-testid={`tp-board-column-${key}`}
              onDragOver={(e) => handleDragOver(e, col.vehicle_id || 'unassigned')}
              onDrop={(e) => handleDrop(e, col)}
            >
              <div className="flex items-center gap-2 mb-1">
                <Truck size={14} className="text-slate-600" />
                <div className="text-xs font-semibold">{col.vehicle.registration}</div>
                <Badge variant="outline" className="text-[10px] ml-auto">{col.job_count}</Badge>
              </div>
              <div className="text-[10px] text-muted-foreground mb-1">
                {col.vehicle.vehicle_type}
                {col.driver && <> · <User size={9} className="inline" /> {col.driver.name}</>}
              </div>
              {col.vehicle_id && col.jobs.length > 0 && (
                <Button size="xs" variant="outline" className="text-[10px] h-6 mb-1 w-full"
                        onClick={() => setPlannerVehicle(col.vehicle)}
                        data-testid={`tp-board-plan-route-${col.vehicle_id}`}>
                  <ArrowRight size={10} className="mr-1" /> Plan route ({col.jobs.length} stops)
                </Button>
              )}
              {col.vehicle_id && (
                <div className="text-[10px] mb-2 space-y-0.5">
                  <div>
                    <span className="tabular-nums">{col.load.pallets}</span>/<span className="tabular-nums">{col.capacity.pallets || '—'}</span> pallets ·{' '}
                    <span className="tabular-nums">{Math.round(col.load.weight_kg)}</span>/<span className="tabular-nums">{Math.round(col.capacity.weight_kg || 0)}</span> kg
                  </div>
                  {col.dispatch_optimisation && (col.dispatch_optimisation.km_saved > 0 || col.dispatch_optimisation.min_saved > 0) && (
                    <div className="text-emerald-700 flex items-center gap-1" data-testid={`tp-board-optimised-${col.vehicle_id}`}>
                      <Lightning size={10} weight="fill" />
                      Auto-optimised on dispatch · saved {col.dispatch_optimisation.km_saved > 0 ? `${col.dispatch_optimisation.km_saved.toFixed(1)} km` : ''}{col.dispatch_optimisation.min_saved > 0 ? ` · ${Math.round(col.dispatch_optimisation.min_saved)} min` : ''}
                    </div>
                  )}
                  {col.late_stops > 0 && (
                    <div className="text-red-700 flex items-center gap-1" data-testid={`tp-board-late-${col.vehicle_id}`}>
                      <Warning size={10} weight="fill" />
                      {col.late_stops} stop(s) will miss their delivery window
                    </div>
                  )}
                  {col.warnings?.length > 0 && (
                    <div className="text-red-700 flex items-start gap-1" data-testid={`tp-board-warning-${key}`}>
                      <Warning size={10} className="mt-0.5 shrink-0" />
                      <div>{col.warnings.join(' · ')}</div>
                    </div>
                  )}
                </div>
              )}
              <div className="space-y-1.5">
                {col.jobs.map((j) => (
                  <Card
                    key={j.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, j, col.vehicle_id)}
                    onDragEnd={handleDragEnd}
                    className={`p-2 cursor-grab active:cursor-grabbing ${dragging?.job?.id === j.id ? 'opacity-40 ring-2 ring-brand' : ''}`}
                    data-testid={`tp-board-card-${j.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] font-mono text-muted-foreground">{j.job_number}</div>
                      <Badge className={`text-[9px] ${STATE_COLORS[j.status] || ''}`}>{j.status}</Badge>
                    </div>
                    <div className="text-xs font-semibold">{j.customer_name || '—'}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {j.delivery_location || j.delivery_address || '—'}
                      {j.dangerous_goods && ' ⚠️ DG'}
                      {j.temperature && j.temperature !== 'ambient' && ` ❄️ ${j.temperature}`}
                    </div>
                    <div className="text-[10px] tabular-nums">
                      {j.pallets}p · {j.weight_kg}kg{j.window_start ? ` · ${j.window_start}–${j.window_end || ''}` : ''}
                    </div>
                    {j.live_window_breach === 'late' && (
                      <div className="mt-0.5 text-[9px] text-red-800 bg-red-50 border border-red-200 rounded px-1 py-0.5 flex items-center gap-1" data-testid={`tp-board-card-late-${j.id}`}>
                        <Warning size={9} weight="fill" /> ETA {j.live_eta ? new Date(j.live_eta).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : ''} — after window {j.window_end}
                      </div>
                    )}
                    {j.status === 'planned' && (
                      <Button size="xs" variant="outline" className="text-[10px] h-6 mt-1" onClick={() => advance(j, 'in_transit')} data-testid={`tp-board-dispatch-${j.id}`}>
                        <ArrowRight size={10} className="mr-1" /> Dispatch
                      </Button>
                    )}
                    {j.status === 'in_transit' && (
                      <Button size="xs" variant="outline" className="text-[10px] h-6 mt-1" onClick={() => advance(j, 'delivered')} data-testid={`tp-board-deliver-${j.id}`}>
                        <ArrowRight size={10} className="mr-1" /> Deliver
                      </Button>
                    )}
                  </Card>
                ))}
                {col.jobs.length === 0 && (
                  <div className="text-[11px] text-muted-foreground italic p-2 text-center">
                    {col.vehicle_id ? 'No jobs assigned' : 'Nothing to plan'}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <RoutePlannerDialog
        open={!!plannerVehicle}
        onClose={() => setPlannerVehicle(null)}
        vehicle={plannerVehicle}
        plannedDate={date}
        onSequenced={load}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// iter218 — Freight Rates tab
// ---------------------------------------------------------------------------
function FreightRatesTab() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const empty = {
    name: '', origin_suburb: '*', destination_suburb: '*',
    per_pallet: 0, per_km: 0, per_kg: 0, base_charge: 0, minimum_charge: 0,
    currency: 'AUD', active: true, valid_from: '', valid_to: '', notes: '',
  };
  const [form, setForm] = useState(empty);
  const load = useCallback(async () => setRows((await listFreightRates()).data.rates), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEdit(null); setForm(empty); setOpen(true); };
  const openEdit = (r) => {
    setEdit(r);
    setForm({ ...empty, ...r });
    setOpen(true);
  };
  const save = async () => {
    try {
      if (edit) await updateFreightRate(edit.id, form);
      else await createFreightRate(form);
      toast.success('Rate saved');
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete rate "${r.name || r.origin_suburb}→${r.destination_suburb}"?`)) return;
    await deleteFreightRate(r.id); load();
  };

  return (
    <div className="space-y-3" data-testid="transport-rates-tab">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          {rows.length} rate card(s) · Use <span className="font-mono">*</span> as a wildcard to catch every origin or destination.
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setImportOpen(true)} data-testid="tp-rate-import-btn"><Plus size={14} className="mr-1" /> Import CSV</Button>
          <Button size="sm" onClick={openNew} data-testid="tp-rate-new-btn"><Plus size={14} className="mr-1" /> New rate</Button>
        </div>
      </div>
      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="p-2">Name</th>
              <th className="p-2">Origin</th>
              <th className="p-2">Destination</th>
              <th className="p-2">Base</th>
              <th className="p-2">/ pallet</th>
              <th className="p-2">/ km</th>
              <th className="p-2">/ kg</th>
              <th className="p-2">Min</th>
              <th className="p-2">Valid</th>
              <th className="p-2">Active</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border" data-testid={`tp-rate-row-${r.id}`}>
                <td className="p-2">{r.name || '—'}</td>
                <td className="p-2 font-mono">{r.origin_suburb || '*'}</td>
                <td className="p-2 font-mono">{r.destination_suburb || '*'}</td>
                <td className="p-2 tabular-nums">${(r.base_charge || 0).toFixed(2)}</td>
                <td className="p-2 tabular-nums">${(r.per_pallet || 0).toFixed(2)}</td>
                <td className="p-2 tabular-nums">${(r.per_km || 0).toFixed(2)}</td>
                <td className="p-2 tabular-nums">${(r.per_kg || 0).toFixed(3)}</td>
                <td className="p-2 tabular-nums">${(r.minimum_charge || 0).toFixed(2)}</td>
                <td className="p-2 text-[10px]" data-testid={`tp-rate-valid-${r.id}`}>
                  {(r.valid_from || r.valid_to)
                    ? `${r.valid_from || '—'} → ${r.valid_to || '—'}`
                    : <span className="text-muted-foreground">always</span>}
                </td>
                <td className="p-2">{r.active ? <Badge className="text-[9px] bg-emerald-100 text-emerald-800 border-emerald-300">yes</Badge> : <Badge variant="outline" className="text-[9px]">no</Badge>}</td>
                <td className="p-2 whitespace-nowrap">
                  <Button variant="ghost" size="xs" onClick={() => openEdit(r)} data-testid={`tp-rate-edit-${r.id}`}>Edit</Button>
                  <Button variant="ghost" size="xs" onClick={() => remove(r)} data-testid={`tp-rate-delete-${r.id}`}><Trash size={12} /></Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={11} className="p-6 text-center text-muted-foreground italic">No rate cards yet — add one to enable auto-pricing.</td></tr>}
          </tbody>
        </table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="tp-rate-dialog">
          <DialogHeader>
            <DialogTitle>{edit ? 'Edit rate card' : 'New rate card'}</DialogTitle>
            <DialogDescription>Rate cards are picked by most-specific-match. Exact suburb wins over wildcard.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2"><Label className="text-xs">Name (optional)</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Metro Sydney → NSW regional" data-testid="tp-rate-form-name" /></div>
            <div><Label className="text-xs">Origin suburb</Label><Input value={form.origin_suburb} onChange={(e) => setForm({ ...form, origin_suburb: e.target.value })} placeholder="Sydney NSW  or  *" data-testid="tp-rate-form-origin" /></div>
            <div><Label className="text-xs">Destination suburb</Label><Input value={form.destination_suburb} onChange={(e) => setForm({ ...form, destination_suburb: e.target.value })} placeholder="Melbourne VIC  or  *" data-testid="tp-rate-form-dest" /></div>
            <div><Label className="text-xs">Base charge</Label><Input type="number" step="0.01" value={form.base_charge} onChange={(e) => setForm({ ...form, base_charge: +e.target.value })} data-testid="tp-rate-form-base" /></div>
            <div><Label className="text-xs">Per pallet</Label><Input type="number" step="0.01" value={form.per_pallet} onChange={(e) => setForm({ ...form, per_pallet: +e.target.value })} data-testid="tp-rate-form-pallet" /></div>
            <div><Label className="text-xs">Per km</Label><Input type="number" step="0.01" value={form.per_km} onChange={(e) => setForm({ ...form, per_km: +e.target.value })} data-testid="tp-rate-form-km" /></div>
            <div><Label className="text-xs">Per kg</Label><Input type="number" step="0.001" value={form.per_kg} onChange={(e) => setForm({ ...form, per_kg: +e.target.value })} data-testid="tp-rate-form-kg" /></div>
            <div><Label className="text-xs">Minimum charge</Label><Input type="number" step="0.01" value={form.minimum_charge} onChange={(e) => setForm({ ...form, minimum_charge: +e.target.value })} data-testid="tp-rate-form-min" /></div>
            <div><Label className="text-xs">Valid from</Label><Input type="date" value={form.valid_from || ''} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} data-testid="tp-rate-form-valid-from" title="Optional — leave empty to be always-valid" /></div>
            <div><Label className="text-xs">Valid to</Label><Input type="date" value={form.valid_to || ''} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} data-testid="tp-rate-form-valid-to" /></div>
            <div className="flex items-end pb-1"><label className="text-xs flex items-center gap-1"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="tp-rate-form-active" /> Active</label></div>
            <div className="col-span-2"><Label className="text-xs">Notes</Label><Input value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="tp-rate-submit">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ImportRatesDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={load} />
    </div>
  );
}

// iter219 — Bulk Rate Import dialog
function ImportRatesDialog({ open, onClose, onImported }) {
  const [csvText, setCsvText] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const sample = 'name,origin_suburb,destination_suburb,base_charge,per_pallet,per_km,per_kg,minimum_charge,currency,active\nMetro Syd,*,Sydney NSW,50,30,2,0.10,80,AUD,true\nMetro Mel,*,Melbourne VIC,45,28,1.9,0.10,75,AUD,true';
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { setCsvText(String(reader.result || '')); setPreview(null); };
    reader.readAsText(f);
  };
  const runPreview = async () => {
    if (!csvText.trim()) return toast.error('Paste or upload a CSV first');
    setBusy(true);
    try { const r = await importRatesPreview(csvText); setPreview(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Preview failed'); }
    finally { setBusy(false); }
  };
  const commit = async () => {
    if (!preview || preview.errors.length > 0) return toast.error('Fix errors first');
    setBusy(true);
    try {
      const r = await importRatesCommit(csvText);
      toast.success(`Imported ${r.data.imported} rate card(s)`);
      onImported?.();
      onClose();
      setCsvText(''); setPreview(null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.detail || 'Import failed'));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="tp-rate-import-dialog">
        <DialogHeader>
          <DialogTitle>Import freight rates from CSV</DialogTitle>
          <DialogDescription>
            Preview first · Commit only if there are zero errors. Required columns: <span className="font-mono">origin_suburb</span> and <span className="font-mono">destination_suburb</span>. Use <span className="font-mono">*</span> as a wildcard.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <input type="file" accept=".csv,text/csv" onChange={onFile} className="text-xs" data-testid="tp-rate-import-file" />
          <textarea
            rows={8}
            value={csvText}
            onChange={(e) => { setCsvText(e.target.value); setPreview(null); }}
            className="w-full border border-slate-300 rounded p-2 text-[11px] font-mono"
            placeholder={sample}
            data-testid="tp-rate-import-textarea"
          />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setCsvText(sample)} data-testid="tp-rate-import-sample">Load sample</Button>
            <Button size="sm" onClick={runPreview} disabled={busy} data-testid="tp-rate-import-preview">Preview</Button>
            <Button size="sm" onClick={commit} disabled={busy || !preview || preview.errors.length > 0} data-testid="tp-rate-import-commit">
              Commit {preview ? `(${preview.valid_rows.length})` : ''}
            </Button>
          </div>
          {preview && (
            <div className="text-xs bg-slate-50 rounded p-2 border border-slate-200" data-testid="tp-rate-import-summary">
              <div>
                <Badge className={preview.errors.length > 0 ? 'bg-red-100 text-red-800 border-red-300' : 'bg-emerald-100 text-emerald-800 border-emerald-300'}>
                  {preview.valid_rows.length} valid / {preview.row_count} total
                </Badge>
                {preview.errors.length > 0 && (
                  <Badge className="ml-2 bg-red-100 text-red-800 border-red-300">{preview.errors.length} error(s)</Badge>
                )}
              </div>
              {preview.errors.length > 0 && (
                <ul className="mt-2 space-y-0.5 text-red-800 font-mono text-[10px] max-h-32 overflow-y-auto">
                  {preview.errors.slice(0, 20).map((err, i) => <li key={i}>• {err}</li>)}
                </ul>
              )}
              {preview.valid_rows.length > 0 && (
                <div className="mt-2 text-[10px] text-slate-600">
                  Ready: {preview.valid_rows.map((r) => `${r.clean.origin_suburb}→${r.clean.destination_suburb}`).slice(0, 5).join(', ')}
                  {preview.valid_rows.length > 5 && ` … +${preview.valid_rows.length - 5} more`}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// iter222 — Empty-Leg Marketplace tab
// ---------------------------------------------------------------------------
function EmptyLegsTab() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ open: 0, booked: 0, total: 0 });
  const [open, setOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const empty = { vehicle_id: '', from_depot_warehouse_id: '', to_depot_warehouse_id: '',
                  from_location: '', to_location: '', date: '', departs_at: '',
                  pallet_capacity: 0, weight_capacity_kg: 0, asking_price: 0, notes: '' };
  const [form, setForm] = useState(empty);
  const [vehicles, setVehicles] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const load = useCallback(async () => {
    const params = statusFilter ? { status: statusFilter } : {};
    const [legs, veh] = await Promise.all([
      listEmptyLegs(params),
      listTransportVehicles(),
    ]);
    setRows(legs.data.legs); setCounts(legs.data.counts);
    setVehicles(veh.data.vehicles);
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Fetch warehouses once for the depot dropdowns.
    (async () => {
      try {
        const { data } = await import('../lib/api').then((m) => m.default.get('/warehouses'));
        setWarehouses(Array.isArray(data) ? data : (data?.warehouses || []));
      } catch { setWarehouses([]); }
    })();
  }, []);

  const create = async () => {
    if (!form.date) return toast.error('Date is required');
    try {
      const r = await createEmptyLeg(form);
      toast.success(`Posted: ${r.data.from_display || r.data.from_location} → ${r.data.to_display || r.data.to_location}`);
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Post failed'); }
  };
  const changeState = async (leg, target_state, extra = {}) => {
    try { await transitionEmptyLeg(leg.id, { target_state, ...extra }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };
  const bookIt = async (leg) => {
    const who = window.prompt('Booked by (customer name / email):');
    if (!who) return;
    const priceStr = window.prompt('Booked price (optional):');
    const price = priceStr ? parseFloat(priceStr) : null;
    await changeState(leg, 'booked', { booked_by: who, booked_price: price });
  };
  const remove = async (leg) => {
    if (!window.confirm(`Delete leg ${leg.from_display || leg.from_location} → ${leg.to_display || leg.to_location}?`)) return;
    await deleteEmptyLeg(leg.id); load();
  };
  const copyShare = async (leg) => {
    try {
      const url = `${window.location.origin}${leg.share_path}`;
      await navigator.clipboard.writeText(url);
      toast.success('Public leg link copied');
    } catch { toast.error('Copy failed'); }
  };

  const STATE_CLR = {
    open: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    reserved: 'bg-amber-100 text-amber-800 border-amber-300',
    booked: 'bg-sky-100 text-sky-800 border-sky-300',
    expired: 'bg-slate-200 text-slate-700 border-slate-300',
    cancelled: 'bg-red-100 text-red-800 border-red-300',
  };

  return (
    <div className="space-y-3" data-testid="transport-legs-tab">
      <div className="flex justify-between items-center gap-2 flex-wrap">
        <div className="text-sm text-muted-foreground">
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 mr-2">{counts.open} open</Badge>
          <Badge className="bg-sky-100 text-sky-800 border-sky-300 mr-2">{counts.booked} booked</Badge>
          <span>{counts.total} total in view · own-tenant marketplace only</span>
        </div>
        <div className="flex gap-2 items-center">
          <select className="h-9 rounded border border-border px-2 text-xs" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="tp-leg-filter">
            <option value="">All statuses</option>
            <option value="open">Open</option>
            <option value="reserved">Reserved</option>
            <option value="booked">Booked</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <Button size="sm" onClick={() => { setForm(empty); setOpen(true); }} data-testid="tp-leg-new-btn"><Plus size={14} className="mr-1" /> Post empty leg</Button>
        </div>
      </div>
      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="p-2">From</th><th className="p-2">To</th><th className="p-2">Date</th><th className="p-2">Depart</th>
              <th className="p-2">Cap</th><th className="p-2">Asking</th><th className="p-2">Status</th>
              <th className="p-2">Booked by</th><th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id} className="border-t border-border" data-testid={`tp-leg-row-${l.id}`}>
                <td className="p-2">{l.from_display || l.from_location || '—'}</td>
                <td className="p-2">{l.to_display || l.to_location || '—'}</td>
                <td className="p-2 tabular-nums">{l.date}</td>
                <td className="p-2 tabular-nums">{l.departs_at || '—'}</td>
                <td className="p-2 tabular-nums text-[10px]">{l.pallet_capacity}p / {l.weight_capacity_kg}kg</td>
                <td className="p-2 tabular-nums">${(l.asking_price || 0).toFixed(2)}</td>
                <td className="p-2"><Badge className={`text-[9px] ${STATE_CLR[l.status] || ''}`} data-testid={`tp-leg-status-${l.id}`}>{l.status}</Badge></td>
                <td className="p-2 text-[10px]">{l.booked_by || '—'}{l.booked_price ? <div>${l.booked_price.toFixed(2)}</div> : null}</td>
                <td className="p-2 whitespace-nowrap">
                  <Button variant="ghost" size="xs" onClick={() => copyShare(l)} data-testid={`tp-leg-share-${l.id}`} title="Copy public leg link"><Copy size={12} /></Button>
                  {l.status === 'open' && <Button variant="outline" size="xs" onClick={() => bookIt(l)} data-testid={`tp-leg-book-${l.id}`}>Book</Button>}
                  {l.status === 'booked' && <Button variant="ghost" size="xs" onClick={() => changeState(l, 'open')} data-testid={`tp-leg-reopen-${l.id}`}>Reopen</Button>}
                  <Button variant="ghost" size="xs" onClick={() => remove(l)} data-testid={`tp-leg-delete-${l.id}`}><Trash size={12} /></Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-muted-foreground italic">No empty legs posted yet — plan a multi-depot run and post the return leg here.</td></tr>}
          </tbody>
        </table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="tp-leg-dialog">
          <DialogHeader>
            <DialogTitle>Post empty leg</DialogTitle>
            <DialogDescription>Advertise a return leg so it doesn't run empty. Own-tenant only.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Vehicle</Label>
              <select className="w-full h-9 rounded border border-border px-2 text-sm" value={form.vehicle_id} onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} data-testid="tp-leg-form-vehicle">
                <option value="">— Optional —</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>{v.registration} · {v.pallet_capacity}p</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Date *</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="tp-leg-form-date" /></div>
            <div><Label className="text-xs">From (depot)</Label>
              <select className="w-full h-9 rounded border border-border px-2 text-sm" value={form.from_depot_warehouse_id} onChange={(e) => setForm({ ...form, from_depot_warehouse_id: e.target.value })} data-testid="tp-leg-form-from">
                <option value="">— Pick or use free-text below —</option>
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">To (depot)</Label>
              <select className="w-full h-9 rounded border border-border px-2 text-sm" value={form.to_depot_warehouse_id} onChange={(e) => setForm({ ...form, to_depot_warehouse_id: e.target.value })} data-testid="tp-leg-form-to">
                <option value="">— Pick or use free-text below —</option>
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">From (free-text)</Label><Input value={form.from_location} onChange={(e) => setForm({ ...form, from_location: e.target.value })} placeholder="e.g. Sydney Airport" /></div>
            <div><Label className="text-xs">To (free-text)</Label><Input value={form.to_location} onChange={(e) => setForm({ ...form, to_location: e.target.value })} placeholder="e.g. Melbourne CBD" /></div>
            <div><Label className="text-xs">Departs at</Label><Input type="time" value={form.departs_at} onChange={(e) => setForm({ ...form, departs_at: e.target.value })} data-testid="tp-leg-form-departs" /></div>
            <div><Label className="text-xs">Asking price</Label><Input type="number" step="0.01" value={form.asking_price} onChange={(e) => setForm({ ...form, asking_price: +e.target.value })} data-testid="tp-leg-form-price" /></div>
            <div><Label className="text-xs">Pallet capacity</Label><Input type="number" value={form.pallet_capacity} onChange={(e) => setForm({ ...form, pallet_capacity: +e.target.value })} data-testid="tp-leg-form-pallets" /></div>
            <div><Label className="text-xs">Weight capacity (kg)</Label><Input type="number" value={form.weight_capacity_kg} onChange={(e) => setForm({ ...form, weight_capacity_kg: +e.target.value })} /></div>
            <div className="col-span-2"><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Refrigerated OK, no DG etc." /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="tp-leg-submit">Post</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// iter223 — Exceptions tab
// ---------------------------------------------------------------------------
function ExceptionsTab() {
  const [data, setData] = useState(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');
  const load = useCallback(async () => {
    const params = {};
    if (from) params.from_date = from;
    if (to) params.to_date = to;
    if (reason) params.reason = reason;
    try { setData((await listExceptions(params)).data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
  }, [from, to, reason]);
  useEffect(() => { load(); }, [load]);

  const SEV_CLR = {
    info: 'bg-slate-100 text-slate-700 border-slate-300',
    warning: 'bg-amber-100 text-amber-800 border-amber-300',
    critical: 'bg-red-100 text-red-800 border-red-300',
  };
  const maxCount = Math.max(1, ...(data?.counts_by_reason || []).map((r) => r.count));

  return (
    <div className="space-y-3" data-testid="transport-exceptions-tab">
      <div className="flex gap-2 items-end flex-wrap">
        <div><Label className="text-xs">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="tp-exc-from" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="tp-exc-to" /></div>
        <div><Label className="text-xs">Reason</Label>
          <select className="h-9 rounded border border-border px-2 text-xs" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="tp-exc-reason-filter">
            <option value="">All reasons</option>
            {(data?.counts_by_reason || []).map((r) => <option key={r.code} value={r.code}>{r.label} ({r.count})</option>)}
          </select>
        </div>
        <div className="text-sm text-muted-foreground ml-auto">{data?.total || 0} exception(s) in view</div>
      </div>

      {/* Reason breakdown bar */}
      <Card className="p-3">
        <div className="text-xs font-semibold mb-2">Exceptions by reason</div>
        <div className="space-y-1" data-testid="tp-exc-bar-chart">
          {(data?.counts_by_reason || []).filter((r) => r.count > 0).map((r) => (
            <div key={r.code} className="flex items-center gap-2 text-[11px]">
              <div className="w-40 shrink-0">{r.label}</div>
              <div className="flex-1 h-4 bg-slate-100 rounded overflow-hidden">
                <div className={`h-full ${r.severity === 'critical' ? 'bg-red-500' : r.severity === 'warning' ? 'bg-amber-500' : 'bg-slate-400'}`} style={{ width: `${(r.count / maxCount) * 100}%` }} />
              </div>
              <div className="w-10 text-right tabular-nums">{r.count}</div>
            </div>
          ))}
          {(!data?.counts_by_reason || data.counts_by_reason.every((r) => r.count === 0)) && (
            <div className="text-xs text-muted-foreground italic py-4 text-center">No exceptions captured yet — kudos to the drivers.</div>
          )}
        </div>
      </Card>

      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="p-2">Job</th><th className="p-2">Customer</th>
              <th className="p-2">Delivery</th><th className="p-2">Reason</th>
              <th className="p-2">Severity</th><th className="p-2">When</th>
              <th className="p-2">Note</th>
            </tr>
          </thead>
          <tbody>
            {(data?.entries || []).map((e) => (
              <tr key={e.job_id} className="border-t border-border" data-testid={`tp-exc-row-${e.job_id}`}>
                <td className="p-2 font-mono font-semibold">{e.job_number}</td>
                <td className="p-2">{e.customer_name || '—'}</td>
                <td className="p-2">{e.delivery_location || '—'}</td>
                <td className="p-2">{e.reason_label}</td>
                <td className="p-2"><Badge className={`text-[9px] ${SEV_CLR[e.severity] || ''}`}>{e.severity}</Badge></td>
                <td className="p-2 text-[10px] tabular-nums">{e.at ? new Date(e.at).toLocaleString('en-AU') : '—'}</td>
                <td className="p-2 text-[10px] max-w-xs">{e.note || <span className="text-muted-foreground">—</span>}</td>
              </tr>
            ))}
            {(!data || data.entries.length === 0) && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground italic">No exceptions in the filter window</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// iter223 — Analytics tab
// ---------------------------------------------------------------------------
function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const load = useCallback(async () => {
    const params = {};
    if (from) params.from_date = from;
    if (to) params.to_date = to;
    try { setData((await getTransportAnalytics(params)).data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const kpi = (label, value, hint) => (
    <Card className="p-3">
      <div className="text-[10px] uppercase text-muted-foreground tracking-wide">{label}</div>
      <div className="text-2xl font-bold tabular-nums" data-testid={`tp-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{value ?? '—'}</div>
      {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
    </Card>
  );

  const k = data?.kpis || {};
  const otdMax = Math.max(1, ...((data?.on_time_trend) || []).map((r) => r.total));
  const cpkMax = Math.max(1, ...((data?.cost_per_km_trend) || []).map((r) => r.cost_per_km));
  const excMax = Math.max(1, ...((data?.exceptions_by_reason) || []).map((r) => r.count));

  return (
    <div className="space-y-3" data-testid="transport-analytics-tab">
      <div className="flex gap-2 items-end flex-wrap">
        <div><Label className="text-xs">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="tp-analytics-from" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="tp-analytics-to" /></div>
        <Button size="sm" variant="outline" onClick={load} data-testid="tp-analytics-refresh"><ArrowClockwise size={12} className="mr-1" /> Refresh</Button>
        <div className="text-xs text-muted-foreground ml-auto">{data?.window?.job_count || 0} job(s) in window</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {kpi('On-time %', k.on_time_pct != null ? `${k.on_time_pct}%` : '—', `${k.on_time_count || 0} of ${k.on_time_denominator || 0}`)}
        {kpi('Median late min', k.median_late_minutes != null ? `${k.median_late_minutes}m` : '—', `${k.late_count || 0} late · median all: ${k.median_deviation_minutes == null ? '—' : k.median_deviation_minutes > 0 ? `${k.median_deviation_minutes}m late` : k.median_deviation_minutes < 0 ? `${Math.abs(k.median_deviation_minutes)}m early` : 'on time'}`)}
        {kpi('Failed %', k.failed_pct != null ? `${k.failed_pct}%` : '—', `${k.failed_count || 0} cancelled`)}
        {kpi('Cost / km', k.cost_per_km != null ? `$${k.cost_per_km}` : '—')}
        {kpi('Cost / pallet', k.cost_per_pallet != null ? `$${k.cost_per_pallet}` : '—')}
        {kpi('Revenue', `$${(k.total_revenue || 0).toFixed(0)}`)}
        {kpi('Cost', `$${(k.total_cost || 0).toFixed(0)}`)}
        {kpi('Margin', `$${(k.margin || 0).toFixed(0)}`, k.margin_pct != null ? `${k.margin_pct}%` : null)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="p-3">
          <div className="text-xs font-semibold mb-2">On-time delivery trend</div>
          <div className="space-y-1" data-testid="tp-analytics-otd-chart">
            {(data?.on_time_trend || []).map((r) => (
              <div key={r.date} className="flex items-center gap-2 text-[10px]">
                <div className="w-16 tabular-nums shrink-0">{r.date}</div>
                <div className="flex-1 h-3 bg-slate-100 rounded overflow-hidden">
                  <div className={`h-full ${r.on_time_pct >= 90 ? 'bg-emerald-500' : r.on_time_pct >= 75 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${r.on_time_pct}%` }} />
                </div>
                <div className="w-12 text-right tabular-nums">{r.on_time_pct}%</div>
                <div className="w-8 text-right text-muted-foreground">·{r.total}</div>
              </div>
            ))}
            {(data?.on_time_trend || []).length === 0 && <div className="text-xs text-muted-foreground italic py-6 text-center">No delivery-window data yet — set window_start/end on jobs.</div>}
          </div>
        </Card>

        <Card className="p-3">
          <div className="text-xs font-semibold mb-2">Cost per km trend</div>
          <div className="space-y-1" data-testid="tp-analytics-cpk-chart">
            {(data?.cost_per_km_trend || []).map((r) => (
              <div key={r.date} className="flex items-center gap-2 text-[10px]">
                <div className="w-16 tabular-nums shrink-0">{r.date}</div>
                <div className="flex-1 h-3 bg-slate-100 rounded overflow-hidden">
                  <div className="h-full bg-sky-500" style={{ width: `${(r.cost_per_km / cpkMax) * 100}%` }} />
                </div>
                <div className="w-16 text-right tabular-nums">${r.cost_per_km}</div>
              </div>
            ))}
            {(data?.cost_per_km_trend || []).length === 0 && <div className="text-xs text-muted-foreground italic py-6 text-center">Waiting for delivered jobs with distance data</div>}
          </div>
        </Card>
      </div>

      <Card className="p-3">
        <div className="text-xs font-semibold mb-2">Exceptions by reason</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1" data-testid="tp-analytics-exc-chart">
          {(data?.exceptions_by_reason || []).map((r) => (
            <div key={r.code} className="flex items-center gap-2 text-[11px]">
              <div className="w-36 shrink-0">{r.label}</div>
              <div className="flex-1 h-3 bg-slate-100 rounded overflow-hidden">
                <div className={`h-full ${r.severity === 'critical' ? 'bg-red-500' : r.severity === 'warning' ? 'bg-amber-500' : 'bg-slate-400'}`} style={{ width: `${(r.count / excMax) * 100}%` }} />
              </div>
              <div className="w-8 text-right tabular-nums">{r.count}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-3">
        <div className="text-xs font-semibold mb-2">Per-driver on-time & deviation</div>
        <div className="overflow-x-auto" data-testid="tp-analytics-by-driver">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-left text-muted-foreground border-b">
                <th className="py-1 pr-2">Driver</th>
                <th className="py-1 pr-2 text-right">Delivered</th>
                <th className="py-1 pr-2 text-right">On-time %</th>
                <th className="py-1 pr-2 text-right">Median late</th>
                <th className="py-1 pr-2 text-right">Median dev.</th>
                <th className="py-1 pr-2 text-right">Late stops</th>
              </tr>
            </thead>
            <tbody>
              {(data?.by_driver || []).map((r) => (
                <tr key={r.driver_id} className="border-b last:border-0" data-testid={`tp-driver-row-${r.driver_id}`}>
                  <td className="py-1 pr-2 font-medium">{r.driver_name}</td>
                  <td className="py-1 pr-2 text-right tabular-nums">{r.delivered}</td>
                  <td className="py-1 pr-2 text-right tabular-nums">
                    {r.on_time_pct != null ? (
                      <span className={r.on_time_pct >= 90 ? 'text-emerald-600' : r.on_time_pct >= 75 ? 'text-amber-600' : 'text-red-600'}>
                        {r.on_time_pct}%
                      </span>
                    ) : '—'}
                  </td>
                  <td className="py-1 pr-2 text-right tabular-nums">
                    {r.median_late_minutes != null ? `${r.median_late_minutes}m` : '—'}
                  </td>
                  <td className="py-1 pr-2 text-right tabular-nums text-muted-foreground">
                    {r.median_deviation_minutes != null ? `${r.median_deviation_minutes}m` : '—'}
                  </td>
                  <td className="py-1 pr-2 text-right tabular-nums">{r.late_count}</td>
                </tr>
              ))}
              {(data?.by_driver || []).length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-xs italic text-muted-foreground">No delivered jobs with an assigned driver in this window.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------
export default function Transport() {
  return (
    <Layout>
      <div className="space-y-4" data-testid="transport-page">
        <div>
          <p className="overline">Logistics</p>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Truck size={24} className="text-brand" weight="fill" />
            Stockaris Transport
          </h1>
          <p className="text-xs text-muted-foreground mt-1 max-w-3xl">
            Bridge the warehouse to the road: transport jobs auto-populate from orders + transfers,
            planners drag them onto trucks, and Stockaris warns before a load busts capacity.
          </p>
        </div>
        <Tabs defaultValue="board">
          <TabsList data-testid="transport-tabs">
            <TabsTrigger value="board" data-testid="tab-transport-board"><Kanban size={14} className="mr-1" /> Board</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="tab-transport-jobs"><Package size={14} className="mr-1" /> Jobs</TabsTrigger>
            <TabsTrigger value="vehicles" data-testid="tab-transport-vehicles"><Truck size={14} className="mr-1" /> Vehicles</TabsTrigger>
            <TabsTrigger value="drivers" data-testid="tab-transport-drivers"><User size={14} className="mr-1" /> Drivers</TabsTrigger>
            <TabsTrigger value="rates" data-testid="tab-transport-rates"><CurrencyDollar size={14} className="mr-1" /> Rates</TabsTrigger>
            <TabsTrigger value="legs" data-testid="tab-transport-legs"><ArrowRight size={14} className="mr-1" /> Marketplace</TabsTrigger>
            <TabsTrigger value="exceptions" data-testid="tab-transport-exceptions"><Warning size={14} className="mr-1" /> Exceptions</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-transport-analytics"><Lightning size={14} className="mr-1" /> Analytics</TabsTrigger>
            <TabsTrigger value="recurring" data-testid="tab-transport-recurring"><Repeat size={14} className="mr-1" /> Recurring</TabsTrigger>
          </TabsList>
          <TabsContent value="board" className="mt-4"><BoardTab /></TabsContent>
          <TabsContent value="jobs" className="mt-4"><JobsTab /></TabsContent>
          <TabsContent value="vehicles" className="mt-4"><VehiclesTab /></TabsContent>
          <TabsContent value="drivers" className="mt-4"><DriversTab /></TabsContent>
          <TabsContent value="rates" className="mt-4"><FreightRatesTab /></TabsContent>
          <TabsContent value="legs" className="mt-4"><EmptyLegsTab /></TabsContent>
          <TabsContent value="exceptions" className="mt-4"><ExceptionsTab /></TabsContent>
          <TabsContent value="analytics" className="mt-4"><AnalyticsTab /></TabsContent>
          <TabsContent value="recurring" className="mt-4"><RecurringTab /></TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

// ============================================================================
// iter232 — TMS Phase 12: Recurring Runs & Milk Runs tab
// ============================================================================
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function RecurringTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewTpl, setPreviewTpl] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await listRecurringTemplates();
      setTemplates(r.data.templates || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setOpen(true); };
  const closeDialog = () => { setOpen(false); setEditing(null); };

  const togglePause = async (t) => {
    try {
      await pauseRecurringTemplate(t.id);
      toast.success(t.active ? 'Template paused' : 'Template resumed');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Pause failed'); }
  };

  const doDelete = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? Existing spawned jobs stay — only the template is removed.`)) return;
    try {
      await deleteRecurringTemplate(t.id);
      toast.success('Template deleted');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const doSpawnToday = async (t) => {
    try {
      const r = await spawnRecurringTemplate(t.id, null, false);
      if (!r.data.ok) toast.warning(`Skipped: ${r.data.reason}`);
      else if (r.data.reused) toast.message(`Already spawned: ${r.data.job_number}`);
      else toast.success(`Spawned ${r.data.job_number}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Spawn failed'); }
  };

  const doPreview = async (t) => {
    setPreviewTpl(t);
    setPreviewRows([]);
    try {
      const r = await previewRecurringTemplate(t.id, 14);
      setPreviewRows(r.data.preview || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Preview failed'); }
  };

  return (
    <div className="space-y-4" data-testid="transport-recurring-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold">Recurring Runs &amp; Milk Runs</h3>
          <p className="text-xs text-muted-foreground">
            Cron-style templates auto-spawn transport jobs each morning. Public holidays respected per country.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="tp-recurring-new-btn">
          <Plus size={14} className="mr-1" /> New template
        </Button>
      </div>

      <div className="overflow-x-auto border rounded-sm">
        <table className="text-sm w-full min-w-[900px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b">
              <th className="p-2">Name</th>
              <th className="p-2">Days</th>
              <th className="p-2">Pickup / Delivery</th>
              <th className="p-2">Window</th>
              <th className="p-2">Status</th>
              <th className="p-2">Last spawned</th>
              <th className="p-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="tp-recurring-table">
            {templates.map((t) => (
              <tr key={t.id} className="border-b last:border-b-0">
                <td className="p-2">
                  <div className="font-bold">{t.name}</div>
                  {t.customer_name && <div className="text-xs text-muted-foreground">→ {t.customer_name}</div>}
                </td>
                <td className="p-2 font-mono text-xs">
                  {(t.days_of_week || []).sort((a, b) => a - b).map((d) => WEEKDAYS[d]).join(' ')}
                  {t.skip_public_holidays && (
                    <div className="text-[10px] text-emerald-700">skips {t.country_code} holidays</div>
                  )}
                </td>
                <td className="p-2 text-xs">
                  {t.pickup_address && <div className="text-muted-foreground truncate max-w-[220px]">↑ {t.pickup_address}</div>}
                  {(t.stops && t.stops.length > 0) ? (
                    <div className="flex items-center gap-1 flex-wrap">
                      <Badge className="bg-sky-100 text-sky-800 border-sky-300 text-[10px]">
                        {t.stops.length}-stop milk run
                      </Badge>
                      <span className="truncate max-w-[180px] text-muted-foreground">
                        ↓ {t.stops[0].address}{t.stops.length > 1 ? ` + ${t.stops.length - 1} more` : ''}
                      </span>
                    </div>
                  ) : (
                    t.delivery_address && <div className="truncate max-w-[220px]">↓ {t.delivery_address}</div>
                  )}
                </td>
                <td className="p-2 font-mono text-xs">
                  {t.window_start || '—'} – {t.window_end || '—'}
                </td>
                <td className="p-2">
                  <Badge className={t.active ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : 'bg-amber-100 text-amber-800 border-amber-300'}>
                    {t.active ? 'active' : 'paused'}
                  </Badge>
                </td>
                <td className="p-2 text-xs text-muted-foreground">
                  {t.last_spawned_at ? new Date(t.last_spawned_at).toLocaleString() : '—'}
                  {t.last_spawn_result?.job_number && (
                    <div className="font-mono">{t.last_spawn_result.job_number}</div>
                  )}
                </td>
                <td className="p-2 text-right">
                  <div className="inline-flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => doPreview(t)} data-testid={`tp-recurring-preview-${t.id}`}>
                      <Calendar size={12} />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => doSpawnToday(t)} data-testid={`tp-recurring-spawn-${t.id}`}>
                      <PlayCircle size={12} />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => togglePause(t)} data-testid={`tp-recurring-pause-${t.id}`}>
                      {t.active ? <Pause size={12} /> : <PlayCircle size={12} />}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid={`tp-recurring-edit-${t.id}`}>
                      edit
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => doDelete(t)} data-testid={`tp-recurring-delete-${t.id}`}>
                      <Trash size={12} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && templates.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center italic text-muted-foreground">
                No recurring templates yet — click "New template" to automate a milk run.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && <RecurringTemplateDialog template={editing} onClose={closeDialog} onSaved={() => { closeDialog(); load(); }} />}
      {previewTpl && <RecurringPreviewDialog template={previewTpl} rows={previewRows} onClose={() => setPreviewTpl(null)} />}
    </div>
  );
}

function RecurringTemplateDialog({ template, onClose, onSaved }) {
  const isEdit = !!template;
  const [name, setName] = useState(template?.name || '');
  const [customerName, setCustomerName] = useState(template?.customer_name || '');
  const [customerEmail, setCustomerEmail] = useState(template?.customer_email || '');
  const [pickupAddress, setPickupAddress] = useState(template?.pickup_address || '');
  const [deliveryAddress, setDeliveryAddress] = useState(template?.delivery_address || '');
  const [pallets, setPallets] = useState(template?.pallets ?? 1);
  const [weightKg, setWeightKg] = useState(template?.weight_kg ?? 0);
  const [pickupTime, setPickupTime] = useState(template?.pickup_time || '09:00');
  const [windowStart, setWindowStart] = useState(template?.window_start || '09:00');
  const [windowEnd, setWindowEnd] = useState(template?.window_end || '17:00');
  const [days, setDays] = useState(new Set(template?.days_of_week || [0, 2, 4]));
  const [startDate, setStartDate] = useState(template?.start_date || '');
  const [endDate, setEndDate] = useState(template?.end_date || '');
  const [skipHolidays, setSkipHolidays] = useState(template?.skip_public_holidays ?? true);
  const [countryCode, setCountryCode] = useState(template?.country_code || 'AU');
  const [stops, setStops] = useState(template?.stops || []);
  const [busy, setBusy] = useState(false);

  const toggleDay = (d) => {
    const nd = new Set(days);
    if (nd.has(d)) nd.delete(d); else nd.add(d);
    setDays(nd);
  };

  const addStop = () => setStops([...stops, { address: '', customer_name: '', pallets: 1, weight_kg: 0, window_end: '' }]);
  const updateStop = (i, patch) => setStops(stops.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const removeStop = (i) => setStops(stops.filter((_, idx) => idx !== i));

  const stopTotals = stops.reduce((acc, s) => {
    acc.pallets += Number(s.pallets || 0);
    acc.weight += Number(s.weight_kg || 0);
    return acc;
  }, { pallets: 0, weight: 0 });

  const submit = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    if (days.size === 0) { toast.error('Pick at least one day'); return; }
    const cleanStops = stops.filter((s) => (s.address || '').trim());
    if (cleanStops.length === 0 && !deliveryAddress.trim()) {
      toast.error('Add a delivery address or at least one stop');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        customer_name: customerName.trim() || null,
        customer_email: customerEmail.trim() || null,
        pickup_address: pickupAddress.trim() || null,
        delivery_address: deliveryAddress.trim() || null,
        pallets: Number(pallets) || 0,
        weight_kg: Number(weightKg) || 0,
        pickup_time: pickupTime || null,
        window_start: windowStart || null,
        window_end: windowEnd || null,
        days_of_week: [...days].sort((a, b) => a - b),
        start_date: startDate || null,
        end_date: endDate || null,
        skip_public_holidays: !!skipHolidays,
        country_code: (countryCode || 'AU').toUpperCase(),
        stops: cleanStops.map((s) => ({
          address: s.address.trim(),
          location: (s.location || '').trim() || null,
          customer_name: (s.customer_name || '').trim() || null,
          customer_email: (s.customer_email || '').trim() || null,
          pallets: Number(s.pallets) || 0,
          cartons: Number(s.cartons) || 0,
          weight_kg: Number(s.weight_kg) || 0,
          cubic_m3: Number(s.cubic_m3) || 0,
          window_start: (s.window_start || '').trim() || null,
          window_end: (s.window_end || '').trim() || null,
          special_instructions: (s.special_instructions || '').trim() || null,
        })),
      };
      if (isEdit) await updateRecurringTemplate(template.id, payload);
      else await createRecurringTemplate(payload);
      toast.success(isEdit ? 'Template updated' : 'Template created');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const singleStopMode = stops.length === 0;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="tp-recurring-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit recurring template' : 'New recurring template'}</DialogTitle>
          <DialogDescription>
            Add stops below to turn this template into a milk run — one job with sequential dropoffs on the same truck.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <label className="col-span-2 flex flex-col gap-1">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Name *</span>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="tp-recurring-form-name" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Customer (parent)</span>
            <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} data-testid="tp-recurring-form-customer" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Customer email</span>
            <Input value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} data-testid="tp-recurring-form-email" />
          </label>
          <label className="col-span-2 flex flex-col gap-1">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Pickup address</span>
            <Input value={pickupAddress} onChange={(e) => setPickupAddress(e.target.value)} data-testid="tp-recurring-form-pickup" />
          </label>
          {singleStopMode && (
            <label className="col-span-2 flex flex-col gap-1">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Delivery address (single-stop)</span>
              <Input value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} data-testid="tp-recurring-form-delivery" />
            </label>
          )}
          {singleStopMode && (
            <>
              <label className="flex flex-col gap-1">
                <span className="text-xs uppercase tracking-widest text-muted-foreground">Pallets</span>
                <Input type="number" value={pallets} onChange={(e) => setPallets(e.target.value)} data-testid="tp-recurring-form-pallets" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs uppercase tracking-widest text-muted-foreground">Weight kg</span>
                <Input type="number" value={weightKg} onChange={(e) => setWeightKg(e.target.value)} data-testid="tp-recurring-form-weight" />
              </label>
            </>
          )}
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Window start</span>
            <Input value={windowStart} onChange={(e) => setWindowStart(e.target.value)} placeholder="09:00" data-testid="tp-recurring-form-window-start" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Window end</span>
            <Input value={windowEnd} onChange={(e) => setWindowEnd(e.target.value)} placeholder="17:00" data-testid="tp-recurring-form-window-end" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Starts on</span>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="tp-recurring-form-start-date" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Ends on</span>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="tp-recurring-form-end-date" />
          </label>
          <div className="col-span-2 flex flex-col gap-1">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Days of week *</span>
            <div className="flex gap-1 flex-wrap">
              {WEEKDAYS.map((label, idx) => (
                <button key={idx} type="button" onClick={() => toggleDay(idx)}
                  className={`px-3 py-1 border text-xs font-bold uppercase tracking-widest ${days.has(idx) ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-white border-neutral-300 text-neutral-700 hover:border-emerald-400'}`}
                  data-testid={`tp-recurring-form-day-${idx}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 col-span-2 text-xs">
            <input type="checkbox" checked={skipHolidays} onChange={(e) => setSkipHolidays(e.target.checked)}
              data-testid="tp-recurring-form-skip-holidays" />
            Skip public holidays in
            <Input value={countryCode} onChange={(e) => setCountryCode(e.target.value)} className="w-16 inline-block" data-testid="tp-recurring-form-country" />
            (ISO country code — AU, US, GB, NZ, …)
          </label>

          {/* Multi-stop milk run editor */}
          <div className="col-span-2 border-t pt-3 mt-2">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-sm font-bold flex items-center gap-2">
                  Multi-stop milk run
                  {stops.length > 0 && (
                    <Badge className="bg-sky-100 text-sky-800 border-sky-300">
                      {stops.length} stop{stops.length === 1 ? '' : 's'} · {stopTotals.pallets}p · {stopTotals.weight}kg
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Add 1+ stops to spawn ONE multi-stop job per date. Totals auto-sum from the stops.
                </p>
              </div>
              <Button type="button" variant="outline" onClick={addStop} data-testid="tp-recurring-form-add-stop">
                <Plus size={12} className="mr-1" /> Add stop
              </Button>
            </div>
            {stops.map((s, i) => (
              <div key={i} className="border rounded-sm p-3 mb-2 bg-neutral-50 grid grid-cols-6 gap-2 items-end text-xs" data-testid={`tp-recurring-form-stop-${i}`}>
                <div className="col-span-6 flex items-center justify-between">
                  <span className="font-bold uppercase tracking-widest text-[10px] text-sky-700">Stop {i + 1}</span>
                  <Button type="button" size="sm" variant="destructive" onClick={() => removeStop(i)}
                    data-testid={`tp-recurring-form-remove-stop-${i}`}>
                    <Trash size={10} />
                  </Button>
                </div>
                <label className="col-span-4 flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Address *</span>
                  <Input value={s.address || ''} onChange={(e) => updateStop(i, { address: e.target.value })}
                    data-testid={`tp-recurring-form-stop-${i}-address`} />
                </label>
                <label className="col-span-2 flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Customer</span>
                  <Input value={s.customer_name || ''} onChange={(e) => updateStop(i, { customer_name: e.target.value })}
                    data-testid={`tp-recurring-form-stop-${i}-customer`} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Pallets</span>
                  <Input type="number" value={s.pallets ?? 0} onChange={(e) => updateStop(i, { pallets: e.target.value })}
                    data-testid={`tp-recurring-form-stop-${i}-pallets`} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Weight kg</span>
                  <Input type="number" value={s.weight_kg ?? 0} onChange={(e) => updateStop(i, { weight_kg: e.target.value })} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Window end</span>
                  <Input value={s.window_end || ''} onChange={(e) => updateStop(i, { window_end: e.target.value })} placeholder="11:00" />
                </label>
                <label className="col-span-3 flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Notes</span>
                  <Input value={s.special_instructions || ''} onChange={(e) => updateStop(i, { special_instructions: e.target.value })} />
                </label>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="tp-recurring-form-submit">
            {busy ? 'Saving…' : (isEdit ? 'Save template' : 'Create template')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecurringPreviewDialog({ template, rows, onClose }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="tp-recurring-preview-dialog">
        <DialogHeader>
          <DialogTitle>Next 14 days — "{template.name}"</DialogTitle>
          <DialogDescription>
            FIRE = a job will spawn on the daily cron. Everything else is skipped with a reason.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[400px] overflow-y-auto text-xs font-mono">
          {rows.length === 0 ? <div className="italic text-muted-foreground">Loading…</div> :
            rows.map((r) => (
              <div key={r.date} className="flex justify-between py-1 border-b">
                <span>{r.date} · {r.weekday}</span>
                <span className={r.fire ? 'text-emerald-700 font-bold' : 'text-muted-foreground'}>
                  {r.fire ? 'FIRE' : `skip: ${r.reason}`}
                </span>
              </div>
            ))
          }
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
